import React, { useState, useEffect } from "react";
import { Modal, Tabs, Button, Spin, Radio, DatePicker, Form, Alert, Tag, Steps, Divider, Input } from 'antd';
import Table from '../../TableResizable';
import {
  BankOutlined, CalendarOutlined, DatabaseOutlined,
  CheckCircleOutlined, LoadingOutlined, EyeOutlined,
  ThunderboltOutlined, EditOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import type { TabsProps } from "antd";
import dayjs from "dayjs";
import SearchModal from "../../SearchModal";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import axios from "axios";
import SweetAlert from "sweetalert2";
import { useAuthHeader, useAuthUser } from "../../../Services/Auth/sessionAuth";
import Utilisateur from "../../../Models/Utilisateur";

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface AgenceConfigDialogProps {
  open: boolean;
  onClose: () => void;
  codeAgence: string;
  libelleAgence: string;
  agenceRattachement: string;
  libelleRattachement: string;
  authHeader: string;
  createBy: string;
  datJour: string;
  onConfigured: () => void;
}

type ConfigMode = "rattachement" | "manuelle" | null;
type ConfigStep = "choix" | "preview" | "done";

interface CompteInterne {
  NUMCPT: string; NOMCPT: string; TYPCPT: string; AGCCPT: string;
}
interface CompteRegroupement {
  Idplan: string; cptregpt: string; codage: string; NOMCPT: string;
}
interface CompteOperation {
  CODOPE: string; NUMCPT: string; CODAGE: string;
}

/* ─── Payload envoyé à ConfigureAgence ───────────────────────────────────── */
interface ConfigureAgencePayload {
  CodeNewAgence : string;
  CreateBy      : string;
  Option        : "preview" | "create";
  Mode          : "auto" | "manual";
  // Comptes AGENCE – mode manuel
  cptLiaiOpCredit ?: string;
  cptLiaiOpDebit  ?: string;
  cptmad          ?: string;
  cptresult       ?: string;
  cpttva1         ?: string;
  cpttva2         ?: string;
  // DATGEN – mode manuel
  DATJOUR     ?: string;
  DATVEIL     ?: string;
  VALDCC      ?: string;
  VALCCC      ?: string;
  VALDCE      ?: string;
  VALCCE      ?: string;
  PRCOMP      ?: string;
  DDCOMP      ?: string;
  FINMOIS     ?: string;
  FINSEM      ?: string;
  FINQUI      ?: string;
  DATM3M      ?: string;
  DATP3M      ?: string;
  CDETAT      ?: string;
  DEBJOUR     ?: string;
  DEBEXE      ?: string;
  CDETATCAI   ?: string;
  DEBJOURCAI  ?: string;
  BatchBO     ?: string;
  BatchCAI    ?: string;
  BatchLine   ?: string;
}

/* ─── Palette ─────────────────────────────────────────────────────────────── */
const palette = {
  bg: "#ffffff",
  surface: "#f6f8fa",
  surfaceHover: "#eef1f5",
  border: "#d0d7de",
  accent: "#1a7f37",
  accentHover: "#166a2e",
  accentLight: "#dafbe1",
  accentGlow: "rgba(26,127,55,0.15)",
  text: "#1f2328",
  textMuted: "#636e7b",
  danger: "#cf222e",
  warning: "#9a6700",
  info: "#444c56",
  infoLight: "#f6f8fa",
  infoBorder: "#d0d7de",
  headerBg: "#f6f8fa",
  headerBorder: "#d0d7de",
};

/* ─── Paramètres de recherche ─────────────────────────────────────────────── */
let searchComptes: ParamsPicklist = {
  CodeAgence: '', Table: "Comptes", Colonne1: "NUMCPT",
  Colonne2: "NOMCPT", Colagence: '101', Choix: 22
};
let searchPlancp: ParamsPicklist = {
  CodeAgence: '', Table: "PLANCP", Colonne1: "IDPLAN",
  Colonne2: "LIBELL", Colagence: '101', Choix: 22
};

/* ─── Carte de choix ──────────────────────────────────────────────────────── */
const ChoiceCard = ({
  icon, title, desc, selected, onClick, color,
}: {
  icon: React.ReactNode; title: string; desc: string;
  selected: boolean; onClick: () => void; color: string;
}) => (
  <div
    onClick={onClick}
    style={{
      flex: 1, padding: "22px 18px", borderRadius: 10,
      border: `2px solid ${selected ? color : palette.border}`,
      background: selected ? `${color}12` : palette.bg,
      cursor: "pointer", transition: "all 0.2s ease",
      boxShadow: selected ? `0 4px 16px ${color}25` : "0 1px 3px rgba(0,0,0,0.06)",
      transform: selected ? "translateY(-2px)" : "none",
    }}
  >
    <div style={{ fontSize: 32, marginBottom: 10, color }}>{icon}</div>
    <div style={{ fontWeight: 700, fontSize: 14, color: palette.text, marginBottom: 5 }}>{title}</div>
    <div style={{ fontSize: 12, color: palette.textMuted, lineHeight: 1.6 }}>{desc}</div>
    {selected && (
      <div style={{ marginTop: 10 }}>
        <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 11 }}>Sélectionné</Tag>
      </div>
    )}
  </div>
);

/* ─── Composant principal ─────────────────────────────────────────────────── */
const AgenceConfigDialog: React.FC<AgenceConfigDialogProps> = ({
  open, onClose, codeAgence, libelleAgence,
  agenceRattachement, libelleRattachement,
  onConfigured, createBy,
}) => {
  const [mode, setMode]       = useState<ConfigMode>(null);
  const [step, setStep]       = useState<ConfigStep>("choix");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab]   = useState("comptes");

  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();

  const [comptesInternes,      setComptesInternes]      = useState<CompteInterne[]>([]);
  const [comptesRegroupement,  setComptesRegroupement]  = useState<CompteRegroupement[]>([]);
  const [comptesOperation,     setComptesOperation]     = useState<CompteOperation[]>([]);

  const [formDateManuelle]       = Form.useForm();
  const [formDatePreview]        = Form.useForm();
  const [formComptesManuels]     = Form.useForm();

  /* ── Reset à l'ouverture ── */
  useEffect(() => {
    if (open) {
      setMode(null);
      setStep("choix");
      setComptesInternes([]);
      setComptesRegroupement([]);
      setComptesOperation([]);
      formDateManuelle.resetFields();
      formDatePreview.resetFields();
      formComptesManuels.resetFields();
    }
  }, [open]);

  /* ── Helper : appel API ConfigureAgence ── */
  const callConfigureAgence = (payload: ConfigureAgencePayload): Promise<any> => {
    return axios.request({
      method: "post",
      maxBodyLength: Infinity,
      url: Data.ApiUrl.ConfigureAgence,          // ← à ajouter dans Data.json
      headers: {
        Authorization: authHeader(),
        "Content-Type": "application/json",
        CypherAgence: Crypter.encrypt(JSON.stringify(payload)),
      },
    });
  };

  /* ── Chargement preview – mode rattachement (option=preview, mode=auto) ── */
  const loadPreview = async () => {
    setLoading(true);
    try {
      const payload: ConfigureAgencePayload = {
        CodeNewAgence : codeAgence,
        CreateBy      : createBy ?? user?.coduti ?? "None",
        Option        : "preview",
        Mode          : "auto",
      };

      const response = await callConfigureAgence(payload);
      const res = JSON.parse(Crypter.decrypt(response.data));

      if (res.StatutCode === 200) {
        const data = res.Result;
        setComptesInternes(data.Table   ?? []);
        setComptesRegroupement(data.Table1 ?? []);
        setComptesOperation(data.Table2   ?? []);

        const d = data.Table3?.[0];
        if (d) {
          formDatePreview.setFieldsValue({
            DATJOUR : d.DATJOUR  ? dayjs(d.DATJOUR)  : null,
            DATVEIL : d.DATVEIL  ? dayjs(d.DATVEIL)  : null,
            PRCOMP  : d.PRCOMP   ? dayjs(d.PRCOMP)   : null,
            DDCOMP  : d.DDCOMP   ? dayjs(d.DDCOMP)   : null,
            FINMOIS : d.FINMOIS  ? dayjs(d.FINMOIS)  : null,
            FINSEM  : d.FINSEM   ? dayjs(d.FINSEM)   : null,
            FINQUI  : d.FINQUI   ? dayjs(d.FINQUI)   : null,
            DATM3M  : d.DATM3M   ? dayjs(d.DATM3M)   : null,
            DATP3M  : d.DATP3M   ? dayjs(d.DATP3M)   : null,
            CDETAT  : d.CDETAT   ?? "OU",
            DEBJOUR : d.DEBJOUR  ? dayjs(d.DEBJOUR)  : null,
            DEBEXE  : d.DEBEXE   ? dayjs(d.DEBEXE)   : null,
          });
        }
        setStep("preview");
      } else {
        SweetAlert.fire({ title: res.Message, icon: "warning" });
      }
    } catch (e: any) {
      console.error(e);
      SweetAlert.fire({ title: "Erreur lors du chargement de l'aperçu", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  /* ── Soumission finale (option=create) ── */
  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      let payload: ConfigureAgencePayload;

      if (mode === "rattachement") {
        /* ── auto : on renvoie les dates éventuellement modifiées par l'utilisateur ── */
        const dates = await formDatePreview.validateFields();

        payload = {
          CodeNewAgence : codeAgence,
          CreateBy      : createBy ?? user?.coduti ?? "None",
          Option        : "create",
          Mode          : "auto",
          // Les dates modifiables (converties en ISO pour le back)
          DATJOUR  : dates.DATJOUR ?.toISOString(),
          DATVEIL  : dates.DATVEIL ?.toISOString(),
          PRCOMP   : dates.PRCOMP  ?.toISOString(),
          DDCOMP   : dates.DDCOMP  ?.toISOString(),
          FINMOIS  : dates.FINMOIS ?.toISOString(),
          FINSEM   : dates.FINSEM  ?.toISOString(),
          FINQUI   : dates.FINQUI  ?.toISOString(),
          DATM3M   : dates.DATM3M  ?.toISOString(),
          DATP3M   : dates.DATP3M  ?.toISOString(),
          CDETAT   : dates.CDETAT,
          DEBJOUR  : dates.DEBJOUR ?.toISOString(),
          DEBEXE   : dates.DEBEXE  ?.toISOString(),
        };
      } else {
        /* ── manual : comptes + dates saisis par l'utilisateur ── */
        const [comptes, dates] = await Promise.all([
          formComptesManuels.validateFields(),
          formDateManuelle.validateFields(),
        ]);

        payload = {
          CodeNewAgence   : codeAgence,
          CreateBy        : createBy ?? user?.coduti ?? "None",
          Option          : "create",
          Mode            : "manual",
          cptmad          : comptes.miseDisposition,
          cptresult       : comptes.resultat,
          cpttva1         : comptes.tvaCollectee,
          cpttva2         : comptes.tvaRecevoir,
          cptLiaiOpDebit  : comptes.liaisonDebits,
          cptLiaiOpCredit : comptes.liaisonCredits,
          DATJOUR  : dates.DATJOUR ?.toISOString(),
          DATVEIL  : dates.DATVEIL ?.toISOString(),
          PRCOMP   : dates.PRCOMP  ?.toISOString(),
          DDCOMP   : dates.DDCOMP  ?.toISOString(),
          FINMOIS  : dates.FINMOIS ?.toISOString(),
          FINSEM   : dates.FINSEM  ?.toISOString(),
          FINQUI   : dates.FINQUI  ?.toISOString(),
          DATM3M   : dates.DATM3M  ?.toISOString(),
          DATP3M   : dates.DATP3M  ?.toISOString(),
          CDETAT   : dates.CDETAT  ?? "53",
          DEBJOUR  : dates.DEBJOUR ?.toISOString(),
          DEBEXE   : dates.DEBEXE  ?.toISOString(),
          CDETATCAI: "53",
        };
      }

      const response = await callConfigureAgence(payload);
      const res = JSON.parse(Crypter.decrypt(response.data));

      if (res.StatutCode === 200) {
        setStep("done");
        setTimeout(() => { onConfigured(); onClose(); }, 1800);
      } else {
        SweetAlert.fire({ title: res.Message ?? "Échec de la configuration", icon: "error" });
      }
    } catch (e: any) {
      if (e?.errorFields) return; // erreur de validation Ant Design, pas d'alerte
      console.error(e);
      SweetAlert.fire({ title: "Une erreur s'est produite ", icon: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Colonnes tableaux (lecture seule) ── */
  const colsInternes = [
    { title: "N° Compte", dataIndex: "NUMCPT", key: "NUMCPT",
      render: (v: string) => <Input size="small" disabled value={v} style={{ width: 180, fontWeight: 600 }} /> },
    { title: "Libellé", dataIndex: "NOMCPT", key: "NOMCPT" },
    { title: "Type", dataIndex: "TYPCPT", key: "TYPCPT", render: () => <Tag color="default">INTERNE</Tag> },
  ];
  const colsRegroupement = [
    { title: "Plan", dataIndex: "Idplan", key: "Idplan", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Compte", dataIndex: "cptregpt", key: "cptregpt",
      render: (v: string) => <Input size="small" disabled value={v} style={{ width: 180, fontWeight: 600 }} /> },
    { title: "Libellé", dataIndex: "NOMCPT", key: "NOMCPT" },
  ];
  const colsOperation = [
    { title: "Code Opération", dataIndex: "CODOPE", key: "CODOPE", render: (v: string) => <Tag color="success">{v}</Tag> },
    { title: "Compte", dataIndex: "NUMCPT", key: "NUMCPT",
      render: (v: string) => <Input size="small" disabled value={v} style={{ width: 180, fontWeight: 600 }} /> },
  ];

  /* ── Onglets ── */
  const tabItems: TabsProps["items"] = [
    {
      key: "comptes",
      label: <span style={{ display: "flex", alignItems: "center", gap: 6 }}><DatabaseOutlined /> Configuration des Comptes</span>,
      children: (
        <div style={{ padding: "4px 0" }}>
          {mode === "rattachement" ? (
            <>
              <Alert type="info" showIcon icon={<InfoCircleOutlined />}
                message="Structure récupérée depuis l'agence principale. Les données sont affichées en lecture seule."
                style={{ marginBottom: 16 }} />
              <SectionHeader icon={<BankOutlined />} title="Comptes Internes" count={comptesInternes.length} />
              <Table dataSource={comptesInternes} columns={colsInternes} size="small" pagination={false} rowKey="NUMCPT" style={{ marginBottom: 20 }} />
              <SectionHeader icon={<DatabaseOutlined />} title="Comptes de Regroupement" count={comptesRegroupement.length} />
              <Table dataSource={comptesRegroupement} columns={colsRegroupement} size="small" pagination={false} rowKey="Idplan" style={{ marginBottom: 20 }} />
              <SectionHeader icon={<ThunderboltOutlined />} title="Comptes d'Opération" count={comptesOperation.length} />
              <Table dataSource={comptesOperation} columns={colsOperation} size="small" pagination={false} rowKey="CODOPE" />
            </>
          ) : (
            <FormComptesManuelImage form={formComptesManuels} />
          )}
        </div>
      ),
    },
    {
      key: "dates",
      label: <span style={{ display: "flex", alignItems: "center", gap: 6 }}><CalendarOutlined /> Dates Comptables & Sécurité</span>,
      children: (
        <div style={{ padding: "4px 0" }}>
          {mode === "rattachement" ? (
            <DatGenFormCard form={formDatePreview} title="Aperçu Éditable des Dates Comptables" />
          ) : (
            <DatGenFormCard form={formDateManuelle} title="Paramètres de Sécurité & Date Valeur (Manuel)" />
          )}
        </div>
      ),
    },
  ];

  /* ── Rendu ── */
  return (
    <Modal
      open={open} onCancel={onClose} width={960} footer={null}
      closable={step !== "done"} maskClosable={false}
      style={{
        content: { padding: 0, borderRadius: 12, overflow: "hidden", boxShadow: "0 16px 48px rgba(0,0,0,0.14)" },
        mask: { background: "rgba(0,0,0,0.45)" },
        header: { display: "none" },
      } as any}
    >
      {/* En-tête */}
      <div style={{
        padding: "18px 24px 16px", borderBottom: `1px solid ${palette.headerBorder}`,
        background: palette.headerBg, display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 8, background: palette.accentLight,
            border: `1px solid ${palette.accent}40`, display: "flex", alignItems: "center",
            justifyContent: "center", color: palette.accent, fontSize: 18,
          }}>
            <BankOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: palette.text, lineHeight: 1.3 }}>
              Configuration de l'Agence
            </div>
            <div style={{ fontSize: 12, color: palette.textMuted }}>
              {codeAgence}{libelleAgence ? ` — ${libelleAgence}` : ""}
            </div>
          </div>
        </div>
        {step !== "done" && (
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: palette.textMuted, fontSize: 20 }}>
            ×
          </button>
        )}
      </div>

      {/* Corps */}
      <div style={{ padding: "24px", background: palette.bg }}>
        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 60, color: palette.accent, marginBottom: 16 }}><CheckCircleOutlined /></div>
            <div style={{ color: palette.text, fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Configuration réussie !</div>
            <div style={{ color: palette.textMuted }}>
              L'agence <strong style={{ color: palette.accent }}>{codeAgence}</strong> a été enregistrée avec succès.
            </div>
          </div>
        ) : (
          <>
            <Steps size="small" current={step === "choix" ? 0 : 1} style={{ marginBottom: 24 }}
              items={[
                { title: <span style={{ fontSize: 12 }}>Choix du mode</span>, icon: <ThunderboltOutlined /> },
                { title: <span style={{ fontSize: 12 }}>Aperçu & Édition</span>, icon: <EyeOutlined /> },
              ]}
            />

            {step === "choix" && (
              <>
                <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                  <ChoiceCard
                    icon={<DatabaseOutlined />}
                    title="Basée sur l'agence principale"
                    desc={`Duplique la structure de ${libelleRattachement || agenceRattachement}. Modifiable depuis les tableaux d'aperçu.`}
                    selected={mode === "rattachement"}
                    onClick={() => setMode("rattachement")}
                    color={palette.accent}
                  />
                  <ChoiceCard
                    icon={<EditOutlined />}
                    title="Nouvelle configuration manuelle"
                    desc="Configurez manuellement l'intégralité des plans de comptes spécifiques de l'agence."
                    selected={mode === "manuelle"}
                    onClick={() => setMode("manuelle")}
                    color={palette.accent}
                  />
                </div>
                {mode && (
                  <div style={{ textAlign: "right" }}>
                    <Button type="primary" size="large" loading={loading}
                      onClick={mode === "rattachement" ? loadPreview : () => setStep("preview")}
                      style={{ background: palette.accent, borderColor: palette.accent, borderRadius: 8, fontWeight: 600 }}>
                      Charger la configuration
                    </Button>
                  </div>
                )}
              </>
            )}

            {step === "preview" && (
              <>
                <Spin spinning={loading} indicator={<LoadingOutlined style={{ color: palette.accent }} />}>
                  <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} type="card" size="small" />
                </Spin>
                <Divider style={{ margin: "20px 0 16px" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Button size="middle" onClick={() => { setStep("choix"); setMode(null); }}>← Retour</Button>
                  <Button type="primary" size="large" loading={submitting}
                    icon={<CheckCircleOutlined />} onClick={handleFinalSubmit}
                    style={{ background: palette.accent, borderColor: palette.accent, borderRadius: 8, fontWeight: 600, minWidth: 180 }}>
                    Valider la configuration
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

/* ─── Sous-composants ─────────────────────────────────────────────────────── */

const SectionHeader = ({ icon, title, count }: { icon: React.ReactNode; title: string; count: number }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, padding: "6px 0", borderBottom: `1px solid ${palette.border}` }}>
    <span style={{ color: palette.accent }}>{icon}</span>
    <span style={{ fontWeight: 600, fontSize: 13, color: palette.text }}>{title}</span>
    <Tag style={{ marginLeft: "auto", fontSize: 11 }}>{count} ligne(s)</Tag>
  </div>
);

const DatGenFormCard = ({ form, title }: { form: any; title: string }) => {
  const fields = [
    { name: "DATJOUR", label: "Date du Jour"               },
    { name: "DATVEIL", label: "Date Veille"                 },
    { name: "PRCOMP",  label: "Première Comptabilisation"   },
    { name: "DDCOMP",  label: "Dernière Comptabilisation"   },
    { name: "FINMOIS", label: "Fin de Mois"                 },
    { name: "FINSEM",  label: "Fin de Semaine"              },
    { name: "FINQUI",  label: "Fin de Quinzaine"            },
    { name: "DATM3M",  label: "Date M3M"                    },
    { name: "DATP3M",  label: "Date P3M"                    },
    { name: "DEBJOUR", label: "Début Journée"               },
    { name: "DEBEXE",  label: "Début Exercice"              },
  ];
  return (
    <Form form={form} layout="vertical">
      <div style={{ border: `1px solid ${palette.border}`, borderRadius: 6, padding: "16px 20px" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: palette.text, borderBottom: `1px solid ${palette.border}`, paddingBottom: 6, marginBottom: 16, textAlign: "center" }}>
          {title}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {fields.map(f => (
            <Form.Item key={f.name} name={f.name}
              label={<span style={{ fontSize: 12, color: palette.textMuted }}>{f.label}</span>}
              style={{ marginBottom: 12 }}>
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          ))}
        </div>
        <Form.Item name="CDETAT" label={<span style={{ fontSize: 12, color: palette.textMuted }}>État</span>}>
          <Radio.Group>
            <Radio value="OU">Ouvert</Radio>
            <Radio value="FE">Fermé</Radio>
          </Radio.Group>
        </Form.Item>
      </div>
    </Form>
  );
};

const FormComptesManuelImage = ({ form }: { form: any }) => (
  <Form form={form} layout="horizontal" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
    <Alert type="info" showIcon icon={<InfoCircleOutlined />}
      message="Vous devriez au préalable créer ces comptes distinctement."
      style={{ marginBottom: 16 }} />
    <div style={{ border: `1px solid ${palette.border}`, borderRadius: 6, padding: "16px 20px" }}>
      <div style={{ fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${palette.border}`, paddingBottom: 6, marginBottom: 16, textAlign: "center" }}>
        Comptes
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 30px" }}>
        <div>
          <SearchModal width={10} Titre="" notRequired label="Compte Mise à Disposition" name="miseDisposition" Parametre={searchComptes} Taille1={'100%'} Taille2={'100%'} form={form} />
          <SearchModal width={10} Titre="" notRequired label="Compte Résultat"            name="resultat"         Parametre={searchComptes} Taille1={'100%'} Taille2={'100%'} form={form} />
          <SearchModal width={10} Titre="" notRequired label="Idplan Chèque Escompté"     name="idplanEscompte"   Parametre={searchPlancp}  Taille1={'100%'} Taille2={'100%'} form={form} />
          <SearchModal width={10} Titre="" notRequired label="Idplan Chèque Encaissement" name="idplanEncaissement" Parametre={searchPlancp} Taille1={'100%'} Taille2={'100%'} form={form} />
        </div>
        <div>
          <SearchModal width={10} Titre="" notRequired label="Compte TVA Collectée"       name="tvaCollectee"     Parametre={searchComptes} Taille1={'100%'} Taille2={'100%'} form={form} />
          <SearchModal width={10} Titre="" notRequired label="Compte TVA à Recevoir"      name="tvaRecevoir"      Parametre={searchComptes} Taille1={'100%'} Taille2={'100%'} form={form} />
          <SearchModal width={10} Titre="" notRequired label="Liaison Opérations Débits"  name="liaisonDebits"    Parametre={searchComptes} Taille1={'100%'} Taille2={'100%'} form={form} />
          <SearchModal width={10} Titre="" notRequired label="Liaison Opérations Crédits" name="liaisonCredits"   Parametre={searchComptes} Taille1={'100%'} Taille2={'100%'} form={form} />
        </div>
      </div>
    </div>
  </Form>
);

export default AgenceConfigDialog;